import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Plus, Pencil, Trash2, ChevronLeft, ChevronRight } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { getMatches, createMatch, updateMatch, deleteMatch, updateLiveScore, getTeamCategories, getAllTeams, getAllLocations, getAllCompetitions } from "@/lib/api";
import { formatDate } from "@/lib/dateUtils";

const statusOptions = ["upcoming", "live", "completed"];

const statusLabels = {
  upcoming: "Aankomend",
  live: "Live",
  completed: "Afgelopen"
};

const initialFormData = {
  home_team_id: "",
  away_team_id: "",
  home_team: "",
  away_team: "",
  home_score: "",
  away_score: "",
  match_date: "",
  match_time: "",
  location_id: "",
  venue: "",
  competition_id: "",
  competition: "",
  team_category: "A",
  status: "upcoming",
  is_home_game: true,
};

export default function AdminMatches() {
  const { category } = useParams();
  const [matches, setMatches] = useState([]);
  const [teamCategories, setTeamCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [locations, setLocations] = useState([]);
  const [competitions, setCompetitions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormData);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const activeCategory = category || (teamCategories[0]?.code || "EERSTE");
  
  // Hardcoded Futsal Gent team - altijd beschikbaar voor alle categorieën
  const futsalGentTeam = {
    id: "futsal-gent-home",
    name: "Futsal Gent",
    city: "Gent",
    logo_url: null,
    category_id: null, // Beschikbaar voor alle categorieën
    is_home: true
  };

  const fetchMatches = useCallback(async () => {
    try {
      // Get matches for the active category
      const response = await getMatches({ category: activeCategory });
      setMatches(response.data);
    } catch (error) {
      console.error("Error fetching matches:", error);
    } finally {
      setLoading(false);
    }
  }, [activeCategory]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [categoriesRes, teamsRes, locationsRes, competitionsRes] = await Promise.all([
          getTeamCategories(),
          getAllTeams(),
          getAllLocations(),
          getAllCompetitions()
        ]);
        setTeamCategories(categoriesRes.data.filter(c => c.is_active));
        setTeams(teamsRes.data);
        setLocations(locationsRes.data);
        setCompetitions(competitionsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();
  }, []);

  // Helper function: Get category ID from code
  const getCategoryIdFromCode = (code) => {
    const category = teamCategories.find(c => c.code === code);
    return category ? category.id : null;
  };

  // Helper function: Filter teams by active category
  const getTeamsForActiveCategory = () => {
    const activeCategoryId = getCategoryIdFromCode(activeCategory);
    return teams.filter(t => 
      t.category_id === activeCategoryId || 
      !t.category_id // Teams zonder categorie zijn beschikbaar voor alle categorieën
    );
  };

  useEffect(() => {
    fetchMatches();
  }, [fetchMatches]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ 
      ...formData, 
      [name]: type === "checkbox" ? checked : value 
    });
  };

  const handleSelectChange = (name, value) => {
    // Auto-fill team name when team_id is selected
    if (name === "home_team_id") {
      if (value === "futsal-gent-home") {
        setFormData(prev => ({ 
          ...prev, 
          home_team_id: value, 
          home_team: "Futsal Gent", 
          home_logo: null 
        }));
      } else {
        const team = teams.find(t => t.id === value);
        if (team) {
          setFormData(prev => ({ 
            ...prev, 
            home_team_id: value, 
            home_team: team.name, 
            home_logo: team.logo_url 
          }));
        }
      }
    } else if (name === "away_team_id") {
      if (value === "futsal-gent-home") {
        setFormData(prev => ({ 
          ...prev, 
          away_team_id: value, 
          away_team: "Futsal Gent", 
          away_logo: null 
        }));
      } else {
        const team = teams.find(t => t.id === value);
        if (team) {
          setFormData(prev => ({ 
            ...prev, 
            away_team_id: value, 
            away_team: team.name, 
            away_logo: team.logo_url 
          }));
        }
      }
    } else if (name === "location_id") {
      const location = locations.find(l => l.id === value);
      if (location) {
        setFormData(prev => ({ ...prev, location_id: value, venue: location.name }));
      }
    } else if (name === "competition_id") {
      const competition = competitions.find(c => c.id === value);
      if (competition) {
        setFormData(prev => ({ ...prev, competition_id: value, competition: competition.name }));
      }
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validatie: Verplichte velden moeten ingevuld zijn
    if (!formData.home_team_id || !formData.away_team_id || !formData.location_id || !formData.competition_id) {
      toast.error("Selecteer een team, locatie en competitie uit de dropdown");
      return;
    }
    
    setSaving(true);

    const data = {
      ...formData,
      home_score: formData.home_score ? parseInt(formData.home_score) : null,
      away_score: formData.away_score ? parseInt(formData.away_score) : null,
      team_category: activeCategory,
    };

    try {
      if (editingId) {
        await updateMatch(editingId, data);
        toast.success("Wedstrijd succesvol bijgewerkt");
      } else {
        await createMatch(data);
        toast.success("Wedstrijd succesvol toegevoegd");
      }
      setDialogOpen(false);
      setFormData({ ...initialFormData, team_category: activeCategory });
      setEditingId(null);
      fetchMatches();
    } catch (error) {
      toast.error("Kon wedstrijd niet opslaan");
      console.error("Error saving match:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (match) => {
    // Normalize date format - extract YYYY-MM-DD from various formats
    let normalizedDate = "";
    if (match.match_date) {
      // Handle both "2026-04-15T00:00:00" and "2026-04-15" formats
      normalizedDate = match.match_date.split("T")[0];
    }
    
    // Normalize time format - ensure HH:MM format
    let normalizedTime = match.match_time || "";
    if (normalizedTime && normalizedTime.length > 5) {
      normalizedTime = normalizedTime.substring(0, 5);
    }
    
    setFormData({
      home_team_id: match.home_team_id || "",
      away_team_id: match.away_team_id || "",
      home_team: match.home_team || "",
      away_team: match.away_team || "",
      home_score: match.home_score?.toString() || "",
      away_score: match.away_score?.toString() || "",
      match_date: normalizedDate,
      match_time: normalizedTime,
      location_id: match.location_id || "",
      venue: match.venue || "",
      competition_id: match.competition_id || "",
      competition: match.competition || "",
      team_category: match.team_category || "",
      status: match.status || "upcoming",
      is_home_game: match.is_home_game ?? true,
    });
    setEditingId(match.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Weet je zeker dat je deze wedstrijd wilt verwijderen?")) return;

    try {
      await deleteMatch(id);
      toast.success("Wedstrijd succesvol verwijderd");
      fetchMatches();
    } catch (error) {
      toast.error("Kon wedstrijd niet verwijderen");
      console.error("Error deleting match:", error);
    }
  };

  const handleScoreUpdate = async (match) => {
    const homeScore = prompt("Voer thuis score in:", match.home_score || "0");
    if (homeScore === null) return;
    
    const awayScore = prompt("Voer uit score in:", match.away_score || "0");
    if (awayScore === null) return;

    const status = prompt("Voer status in (live/completed):", match.status);
    if (status === null) return;

    try {
      await updateLiveScore(match.id, parseInt(homeScore), parseInt(awayScore), status);
      toast.success("Score bijgewerkt");
      fetchMatches();
    } catch (error) {
      toast.error("Kon score niet bijwerken");
      console.error("Error updating score:", error);
    }
  };

  const openNewDialog = () => {
    setFormData({ ...initialFormData, team_category: activeCategory });
    setEditingId(null);
    setDialogOpen(true);
  };

  const currentIndex = teamCategories.findIndex(c => c.code === activeCategory);
  const prevCategory = currentIndex > 0 ? teamCategories[currentIndex - 1] : null;
  const nextCategory = currentIndex < teamCategories.length - 1 ? teamCategories[currentIndex + 1] : null;
  const currentCategoryName = teamCategories.find(c => c.code === activeCategory)?.name || activeCategory;

  return (
    <div data-testid="admin-matches-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Chivo']">Wedstrijden - {currentCategoryName}</h1>
          <p className="text-slate-600">Beheer wedstrijden en resultaten voor {currentCategoryName}</p>
        </div>
        <Button onClick={openNewDialog} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-match-btn">
          <Plus className="h-4 w-4 mr-2" />
          Wedstrijd Toevoegen
        </Button>
      </div>

      {/* Team Navigation */}
      <div className="flex items-center justify-between mb-6 p-4 bg-slate-100 rounded-lg">
        <div className="flex gap-2">
          {prevCategory && (
            <Button asChild variant="outline" size="sm" className="border-slate-300 text-slate-700">
              <Link to={`/admin/matches/${prevCategory.code}`}>
                <ChevronLeft className="h-4 w-4 mr-1" />
                {prevCategory.name}
              </Link>
            </Button>
          )}
        </div>
        <div className="flex gap-2 flex-wrap justify-center">
          {teamCategories.map((cat) => (
            <Button
              key={cat.id}
              asChild
              variant={cat.code === activeCategory ? "default" : "outline"}
              size="sm"
              className={cat.code === activeCategory ? "bg-blue-600 text-white" : "border-slate-300 text-slate-700"}
            >
              <Link to={`/admin/matches/${cat.code}`} data-testid={`team-nav-${cat.code}`}>
                {cat.code}
              </Link>
            </Button>
          ))}
        </div>
        <div className="flex gap-2">
          {nextCategory && (
            <Button asChild variant="outline" size="sm" className="border-slate-300 text-slate-700">
              <Link to={`/admin/matches/${nextCategory.code}`}>
                {nextCategory.name}
                <ChevronRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          )}
        </div>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50">
                  <TableHead>Datum</TableHead>
                  <TableHead>Thuis</TableHead>
                  <TableHead>Score</TableHead>
                  <TableHead>Uit</TableHead>
                  <TableHead>Locatie</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Acties</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8">Laden...</TableCell>
                  </TableRow>
                ) : matches.length > 0 ? (
                  matches.map((match) => (
                    <TableRow key={match.id} data-testid={`match-row-${match.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{formatDate(match.match_date)}</p>
                          <p className="text-sm text-slate-500">{match.match_time}</p>
                        </div>
                      </TableCell>
                      <TableCell className="font-medium">{match.home_team}</TableCell>
                      <TableCell>
                        {match.home_score !== null ? (
                          <span className="font-bold">
                            {match.home_score} - {match.away_score}
                          </span>
                        ) : (
                          <span className="text-slate-400">vs</span>
                        )}
                      </TableCell>
                      <TableCell className="font-medium">{match.away_team}</TableCell>
                      <TableCell className="text-slate-500">{match.venue}</TableCell>
                      <TableCell>
                        <Badge className={
                          match.status === "live" ? "bg-red-500" :
                          match.status === "completed" ? "bg-green-500" :
                          "bg-blue-500"
                        }>
                          {statusLabels[match.status] || match.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleScoreUpdate(match)}
                            title="Score Bijwerken"
                            data-testid={`score-btn-${match.id}`}
                          >
                            ⚽
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleEdit(match)}
                            data-testid={`edit-btn-${match.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => handleDelete(match.id)}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`delete-btn-${match.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                      Geen wedstrijden gevonden voor {activeCategory} team. Voeg je eerste wedstrijd toe!
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? "Wedstrijd Bewerken" : `Wedstrijd Toevoegen - ${activeCategory} Team`}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Thuisploeg *</Label>
                <Select value={formData.home_team_id} onValueChange={(v) => handleSelectChange("home_team_id", v)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer thuisploeg" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Futsal Gent - altijd bovenaan */}
                    {formData.away_team_id !== "futsal-gent-home" && (
                      <SelectItem value="futsal-gent-home">
                        Futsal Gent (Gent)
                      </SelectItem>
                    )}
                    {/* Andere teams uit database - gefilterd op categorie */}
                    {getTeamsForActiveCategory()
                      .filter(t => t.id !== formData.away_team_id)
                      .map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} {team.city ? `(${team.city})` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Uitploeg *</Label>
                <Select value={formData.away_team_id} onValueChange={(v) => handleSelectChange("away_team_id", v)} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecteer uitploeg" />
                  </SelectTrigger>
                  <SelectContent>
                    {/* Futsal Gent - altijd bovenaan */}
                    {formData.home_team_id !== "futsal-gent-home" && (
                      <SelectItem value="futsal-gent-home">
                        Futsal Gent (Gent)
                      </SelectItem>
                    )}
                    {/* Andere teams uit database - gefilterd op categorie */}
                    {getTeamsForActiveCategory()
                      .filter(t => t.id !== formData.home_team_id)
                      .map((team) => (
                        <SelectItem key={team.id} value={team.id}>
                          {team.name} {team.city ? `(${team.city})` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="home_score">Thuis Score</Label>
                <Input
                  id="home_score"
                  name="home_score"
                  type="number"
                  value={formData.home_score}
                  onChange={handleChange}
                  data-testid="match-home-score"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="away_score">Uit Score</Label>
                <Input
                  id="away_score"
                  name="away_score"
                  type="number"
                  value={formData.away_score}
                  onChange={handleChange}
                  data-testid="match-away-score"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="match_date">Datum</Label>
                <Input
                  id="match_date"
                  name="match_date"
                  type="date"
                  value={formData.match_date}
                  onChange={handleChange}
                  required
                  data-testid="match-date"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="match_time">Tijd</Label>
                <Input
                  id="match_time"
                  name="match_time"
                  type="time"
                  value={formData.match_time}
                  onChange={handleChange}
                  required
                  data-testid="match-time"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Locatie *</Label>
              <Select value={formData.location_id} onValueChange={(v) => handleSelectChange("location_id", v)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer locatie" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((location) => (
                    <SelectItem key={location.id} value={location.id}>
                      {location.name} {location.city ? `- ${location.city}` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Competitie *</Label>
              <Select value={formData.competition_id} onValueChange={(v) => handleSelectChange("competition_id", v)} required>
                <SelectTrigger>
                  <SelectValue placeholder="Selecteer competitie" />
                </SelectTrigger>
                <SelectContent>
                  {competitions.map((comp) => (
                    <SelectItem key={comp.id} value={comp.id}>
                      {comp.name} ({comp.season})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Status</Label>
              <Select 
                value={formData.status} 
                onValueChange={(v) => handleSelectChange("status", v)}
              >
                <SelectTrigger data-testid="match-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statusOptions.map((status) => (
                    <SelectItem key={status} value={status}>{statusLabels[status] || status}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuleren
              </Button>
              <Button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="match-save-btn">
                {saving ? "Opslaan..." : "Wedstrijd Opslaan"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
